import React, { FC } from "react";

import { DataViewKey, useShare } from "@keepeek/commons";
import { Box } from "@mui/material";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";

import { Type, Widget } from "../../../models/configuration/definitions/widget";
import { frontEditState } from "../../../providers/frontEdit/atoms/frontEdit";
import useFrontEditEditable from "../../../providers/frontEdit/hooks/useFrontEditEditable";
import AssociationsWidget from "../AssociationsWidget";
import BasketsWidget from "../BasketsWidget";
import CustomWidget from "../CustomWidget";
import EditoWidget from "../EditoWidget";
import KAttachments from "../KAttachments";
import KFooterWidget from "../KFooter";
import KHeaderWidget from "../KHeaderWidget";
import KImageWidget from "../KImageWidget";
import { translateKImageWidgetConfiguration } from "../KImageWidget/utils";
import MenuWidget from "../KMenuWidget";
import KMosaicWidget from "../KMosaicWidget";
import KNewsWidget from "../KNewsWidget";
import ReportWidget from "../KReportWidget";
import KSearchWidget from "../KSearchWidget";
import KSectionTitleWidget from "../KSectionTitleWidget";
import KSheetWidget from "../KSheetWidget";
import KTabsWidget from "../KTabsWidget";
import KVideoWidget from "../KVideoWidget";
import { WidgetConfigurations } from "../models";
import SpacerWidget from "../SpacerWidget";
import { getWidgetKey } from "../utils/getWidgetKey";
import { isWidgetUnsupportedInUnitaryShare } from "./utils";

export type KWidgetsProps = {
  widgets: Widget[] | undefined;
  widgetConfigurations: WidgetConfigurations | undefined;
  dataViewKey: DataViewKey;
};

export const KWidgets: FC<React.PropsWithChildren<KWidgetsProps>> = function ({
  widgets,
  widgetConfigurations,
  dataViewKey,
}) {
  if (!widgetConfigurations) {
    return null;
  }
  return (
    <Box
      className={clsx("KWidgets")}
      sx={{
        margin: 0,
        minHeight: "100%",
        alignItems: "stretch",
        justifyContent: "stretch",
        display: "flex",
        "& > *": {
          width: "100%",
        },
        flexDirection: "column",
      }}
    >
      {widgets?.map((widget, i) => {
        const widgetKey = getWidgetKey(widget);
        if (!widgetKey) {
          return null;
        }
        const config = widgetConfigurations && widgetConfigurations[widgetKey];
        if (!config) {
          return null;
        }
        return (
          // Add i to the key because we can have two widgetKey identic in the map (multiple spacer for exemple)
          // And if the key is similar it will break React from rendering it correctly
          <Box
            key={`${widgetKey}-${i}`}
            className={clsx("KWidget", widget.classNames)}
            sx={{
              display: "flex",
              flex: 1,
              // When we use the "Sheet" widget we want to have a column layout so that the text does not overflow to the right and generates scrolling in the detailed media
              ...(widget.type === Type.Sheet && { flexDirection: "column" }),
            }}
          >
            <KWidget type={widget.type} configuration={config} dataViewKey={dataViewKey} />
          </Box>
        );
      })}
    </Box>
  );
};

type KWidgetProps = {
  type: Type;
  configuration: any;
  dataViewKey: DataViewKey;
};
const KWidget: FC<React.PropsWithChildren<KWidgetProps>> = function ({
  type,
  configuration,
  dataViewKey,
}) {
  const frontEdit = useRecoilValue(frontEditState);
  const { widgetConfiguration, FrontEditWidgetWrapper } = useFrontEditEditable(
    type,
    configuration.widgetId,
  );
  const router = useRouter();
  const { isUnitaryShareElement } = useShare(router.pathname, router.query.tokenId);
  const frontEditConfig = { ...configuration, ...widgetConfiguration };

  if (isUnitaryShareElement && isWidgetUnsupportedInUnitaryShare(type)) {
    return null;
  }

  switch (type) {
    case Type.Mosaic:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KMosaicWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <KMosaicWidget {...configuration} />
      );
    case Type.Search:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KSearchWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <KSearchWidget {...configuration} />
      );
    case Type.Footer:
      const footerConfiguration = widgetConfiguration
        ? {
            ...configuration,
            footerConfiguration: {
              ...configuration.footerConfiguration,
              ...widgetConfiguration,
            },
          }
        : configuration;
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KFooterWidget {...footerConfiguration} />
        </FrontEditWidgetWrapper>
      ) : (
        <KFooterWidget {...configuration} />
      );
    case Type.Attachments:
      return <KAttachments {...configuration} />;
    case Type.Tabs:
      return <KTabsWidget {...configuration} />;
    case Type.Sheet:
      return <KSheetWidget {...configuration} />;
    case Type.Image:
      const imageConfiguration = widgetConfiguration
        ? translateKImageWidgetConfiguration(frontEditConfig)
        : configuration;
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KImageWidget {...imageConfiguration} />
        </FrontEditWidgetWrapper>
      ) : (
        <KImageWidget {...configuration} />
      );
    case Type.Edito:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <EditoWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <EditoWidget {...configuration} />
      );
    case Type.Header:
      // Front edit config is set on useKHeaderWidgetSelect
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KHeaderWidget {...configuration} dataViewKey={dataViewKey} />
        </FrontEditWidgetWrapper>
      ) : (
        <KHeaderWidget {...configuration} dataViewKey={dataViewKey} />
      );
    case Type.Associations:
      return <AssociationsWidget {...configuration} />;
    case Type.Spacer:
      return <SpacerWidget {...configuration} />;
    case Type.Custom:
      return <CustomWidget {...configuration} />;
    case Type.Video:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KVideoWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <KVideoWidget {...configuration} />
      );
    case Type.News:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KNewsWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <KNewsWidget {...configuration} />
      );
    case Type.SectionTitle:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <KSectionTitleWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <KSectionTitleWidget {...configuration} />
      );
    case Type.Menu:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <MenuWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <MenuWidget {...configuration} />
      );
    case Type.Report:
      return frontEdit ? (
        <FrontEditWidgetWrapper>
          <ReportWidget {...frontEditConfig} />
        </FrontEditWidgetWrapper>
      ) : (
        <ReportWidget {...configuration} />
      );
    case Type.Baskets:
      return <BasketsWidget {...configuration} />;
    default:
      return null;
  }
};
